<template>
    <div class="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-8 lg:max-w-7xl lg:px-8 relative">
        <div id="dropdown"/>
        <Head title="Звіт"/>
        <Header pageName="Кабінет" :cart-count="cartCount"/>
        <filters-modal
            title="Кабінет охоронця"
            :show="showModalFilter"
            :params="form.params"
            @update:form="applyFilter"
        />

        <!-- Legend + filter button -->
        <div class="flex flex-col md:flex-row mt-4 items-start md:items-center md:justify-between">
            <div class="p-4 bg-white rounded-md shadow">
                <p><span class="inline-block w-4 h-4 active-calendar-item mr-2"></span> Замовлення оплачено</p>
                <p><span class="inline-block w-4 h-4 green-calendar-item mr-2"></span> Замовлення не оплачено</p>
                <p><span class="inline-block w-4 h-4 bg-teal-500 mr-2"></span> Замовлення виконано</p>
                <p><span class="inline-block w-4 h-4 bg-red-400 mr-2"></span> Замовлення скасовано</p>
            </div>
            <button
                @click="openModal"
                class="mt-3 md:mt-0 flex items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center px-4"
            >
                Фільтри
            </button>
        </div>

        <!-- Desktop Table -->
        <div class="hidden md:block w-full bg-white rounded-md shadow overflow-x-auto mt-4">
            <table class="w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                    <th class="pb-4 pt-6 px-2 w-table-details w-1">Деталі</th>
                    <th class="pb-4 pl-4 pt-6 cursor-pointer" @click.prevent="sortOrders('order.id')">
                        Номер
                        <icon
                            v-if="form.sort.sortBy === 'order.id'"
                            name="arrow"
                            :class="{'transform rotate-180': form.sort.sortOrder === 'asc'}"
                        />
                    </th>
                    <th class="pb-4 pt-6 px-2 cursor-pointer" @click.prevent="sortOrders('start_date')">
                        Дата
                        <icon
                            v-if="form.sort.sortBy === 'start_date'"
                            name="arrow"
                            :class="{'transform rotate-180': form.sort.sortOrder === 'asc'}"
                        />
                    </th>
                    <th class="pb-4 pt-6 px-2 cursor-pointer" @click.prevent="sortOrders('order.name')">
                        Ім'я
                        <icon
                            v-if="form.sort.sortBy === 'order.name'"
                            name="arrow"
                            :class="{'transform rotate-180': form.sort.sortOrder === 'asc'}"
                        />
                    </th>
                    <th class="pb-4 pt-6 px-2 w-table-place">Назва місця</th>
                    <th class="pb-4 pt-6 px-2 w-table-lake">Озеро</th>
                    <th class="pb-4 pt-6 px-2 w-table-type">Тип</th>
                    <th class="pb-4 pt-6 px-2 w-table-sum">Сума</th>
                </tr>
                <template v-for="order in orders.data" :key="order.id">
                    <tr
                        class="hover:shadow-lg focus-within:shadow-lg h-10 relative"
                        :class="[
                        {'bg-red-400' :order.is_cancel},
                        {'bg-teal-500' :order.is_done && !order.is_cancel},
                        {'active-calendar-item' : order.is_payed && !order.is_done},
                        {'green-calendar-item' : !order.is_payed && !order.is_done}
                        ]"
                    >
                        <td class="border-t w-table-details w-1">
                            <icon
                                name="arrow"
                                :class="{'transform rotate-180': openDetail === order.id}"
                                role="button"
                                @click="showDetails(order.id)"
                            />
                        </td>
                        <td class="border-t">
                            <p @click="showDetails(order.id)" class="flex items-center pl-4 py-4 focus:text-indigo-500">
                                {{ order.liqPayId }}
                            </p>
                        </td>
                        <td class="border-t">
                            <p @click="showDetails(order.id)"
                               class="flex items-center focus:text-indigo-500 cursor-pointer">
                                {{ formattedDate(order.start_date) }}
                            </p>
                        </td>
                        <td class="border-t">
                            <p class="flex items-center px-2 py-4 focus:text-indigo-500">
                                {{ order.name }}
                            </p>
                        </td>
                        <td class="border-t w-table-place">
                            <p class="flex items-center px-2 py-4 focus:text-indigo-500">
                                {{ order.point?.name ?? '' }}
                            </p>
                        </td>
                        <td class="border-t w-table-lake">
                            <p class="flex items-center px-2 py-4 focus:text-indigo-500">
                                {{ order.lake.name }}
                            </p>
                        </td>
                        <td class="border-t w-table-type">
                            <p class="flex items-center focus:text-indigo-500">
                                {{ order.type.name }}
                            </p>
                        </td>
                        <td class="border-t w-table-sum">
                            <p class="flex items-center focus:text-indigo-500">
                                {{ order.totalSum }} грн
                            </p>
                        </td>
                    </tr>
                    <tr v-if="openDetail === order.id" :key="'details-' + order.id">
                        <td v-if="detailsOrder" colspan="8">
                            <order-detail :order="detailsOrder"/>
                        </td>
                    </tr>
                </template>
                <tr v-if="orders.data.length === 0">
                    <td class="px-2 p-4 border-t" colspan="4">Замовлень немає</td>
                </tr>
            </table>
        </div>

        <!-- Mobile Accordion -->
        <div class="md:hidden mt-4">
            <div v-if="orders.data.length === 0" class="bg-white rounded-md shadow p-4 text-gray-500">
                Замовлень немає
            </div>
            <div
                v-for="order in orders.data"
                :key="order.id"
                class="bg-white rounded-md shadow mb-3"
            >
                <div
                    class="p-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                    :class="[
                        {'bg-red-400': order.is_cancel},
                        {'bg-teal-500': order.is_done && !order.is_cancel},
                        {'active-calendar-item': order.is_payed && !order.is_done},
                        {'green-calendar-item': !order.is_payed && !order.is_done}
                    ]"
                    @click="showDetails(order.id)"
                >
                    <div>
                        <p class="font-bold" :class="(!order.is_payed && !order.is_done) ? 'text-gray-900' : 'text-white'">Замовлення #{{ order.liqPayId }}</p>
                        <p class="text-sm" :class="(!order.is_payed && !order.is_done) ? 'text-gray-700' : 'text-white/90'">{{ order.name }}</p>
                        <p class="text-xs mt-0.5" :class="(!order.is_payed && !order.is_done) ? 'text-gray-600' : 'text-white/80'">{{ formattedDate(order.start_date) }}</p>
                    </div>
                    <div class="flex items-center">
                        <span class="text-xs font-bold mr-2" :class="(!order.is_payed && !order.is_done) ? 'text-gray-700' : 'text-white'">
                            <template v-if="order.is_cancel">Скасовано</template>
                            <template v-else-if="order.is_done">Виконано</template>
                            <template v-else-if="order.is_payed">Оплачено</template>
                            <template v-else>Неоплачено</template>
                        </span>
                        <icon
                            name="arrow"
                            :class="[{'transform rotate-180': openDetail === order.id}, (!order.is_payed && !order.is_done) ? 'text-gray-600' : 'text-white']"
                            class="w-5 h-5"
                        />
                    </div>
                </div>

                <div v-show="openDetail === order.id" class="p-4 bg-gray-50">
                    <div class="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-4">
                        <span class="font-bold text-gray-700">Місце:</span>
                        <span class="text-gray-900">{{ order.point?.name ?? '-' }}</span>
                        <span class="font-bold text-gray-700">Озеро:</span>
                        <span class="text-gray-900">{{ order.lake.name }}</span>
                        <span class="font-bold text-gray-700">Тип:</span>
                        <span class="text-gray-900">{{ order.type.name }}</span>
                        <span class="font-bold text-gray-700">Сума:</span>
                        <span class="text-gray-900">{{ order.totalSum }} грн</span>
                    </div>
                    <order-detail v-if="detailsOrder && detailsOrder.id === order.id" :order="detailsOrder"/>
                </div>
            </div>
        </div>

        <pagination class="mt-6" :links="orders.links" />
    </div>
</template>

<script>
import {Head, Link} from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import FiltersModal from "@/Component/FiltersModal"
import Accordion from "@/Component/Accordion"
import Header from "@/Component/Header"
import OrderDetail from "@/Component/OrderDetail"
import throttle from 'lodash/throttle'
import Pagination from '@/Shared/Pagination'
import Checkbox from "@/Shared/Checkbox"

export default {
    components: {
        Checkbox,
        Head,
        Header,
        Icon,
        Link,
        Pagination,
        FiltersModal,
        Accordion,
        OrderDetail
    },
    props: {
        cartCount: {
            type: Number,
            default: 0
        },
        filters: Object,
        orders: Object,
        sort: {
            type: Object,
            default: {
                sortOrder: 'desc',
                sortBy: 'start_date'
            }
        },
    },
    data() {
        return {
            showModalFilter: false,
            form: {
                page: this.filters.page,
                params: this.filters.params,
                sort: this.sort,
            },
            openDetail: null
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.$route('cabinet.watchman'), pickBy(this.form), {
                    preserveState: true,
                    preserveScroll: true,
                })
            }, 150),
        },
    },
    computed: {
        detailsOrder() {
            return this.orders.data.find(item => item.id === this.openDetail)
        }
    },
    methods: {
        showDetails(id) {
            if (!this.openDetail || this.openDetail !== id) {
                this.openDetail = id
            } else {
                this.openDetail = null
            }
        },
        openModal() {
            this.showModalFilter = !this.showModalFilter;
        },
        reset() {
            this.form = {
                search: null,
                params: null,
                sort: this.sort,
            }
        },
        formattedDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('uk-UK', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        applyFilter($event) {
            const newFormWithoutPage = {...this.form, params: {...this.form.params, page: undefined}};
            const oldFormWithoutPage = {...$event, params: {...$event.params, page: undefined}};
            if (JSON.stringify(newFormWithoutPage) !== JSON.stringify(oldFormWithoutPage)) {
                this.form.params = $event
            }
        },
        sortOrders(sort) {
            this.form.sort.sortBy = sort
            if (this.form.sort.sortOrder === 'asc') {
                this.form.sort.sortOrder = 'desc'
            } else {
                this.form.sort.sortOrder = 'asc'
            }
        },
    }
}
</script>
<style scoped>
:deep(.scale-125) {
    width: 20px;
    height: 20px;
}
</style>
